/**
 * GroupPostsTab — every post from the groups the member belongs to, pinned
 * posts first (mobile groupPostsTab parity).
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useGroup } from '@wlb/shared/context/GroupContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { GroupPostsFeed } from './GroupPostsFeed';
import { useScrollRestore } from './useScrollRestore';

export function GroupPostsTab() {
  const { t } = useTranslation();
  const {
    groupPosts,
    groupPostsLoading,
    groupPostsError,
    groupPostsLoaded,
    pinnedPostMap,
    fetchGroupPosts,
  } = useGroup();
  useScrollRestore('groups:posts', !groupPostsLoading || groupPosts.length > 0);

  const loadPosts = useCallback(() => {
    fetchGroupPosts().catch(() => undefined);
  }, [fetchGroupPosts]);

  useEffect(loadPosts, [loadPosts]);

  /** Member-initiated reload — forced past the per-scope cache key. */
  const refresh = useCallback(() => {
    fetchGroupPosts(null, { force: true }).catch(() => undefined);
  }, [fetchGroupPosts]);

  const sortedPosts = useMemo(() => {
    const pinnedIds = new Set(Object.values(pinnedPostMap).filter(Boolean));
    if (pinnedIds.size === 0) return groupPosts;
    return [...groupPosts].sort((a, b) => Number(pinnedIds.has(b.id)) - Number(pinnedIds.has(a.id)));
  }, [groupPosts, pinnedPostMap]);

  return (
    <GroupPostsFeed
      posts={sortedPosts}
      loading={groupPostsLoading}
      error={groupPostsError}
      loaded={groupPostsLoaded}
      onRetry={refresh}
      emptyMessage={t('home.myGroups.emptyPosts')}
    />
  );
}
